/**
 * Marketing templates — 5 fill-in-the-blank documents.
 *
 * Every one of these turns on the same three things, which founders routinely collapse into one:
 *   ownership — the asset is the brand's outright, by present assignment
 *   licence   — someone else owns it and the brand may use it, in named channels, for a named period
 *   access    — the account, the pixel, the audience and the history the work actually lives inside
 * A brand can own the copyright in a video and still have no right to run it as an ad, and can hold
 * a perfect licence to an image while the agency owns the ad account it would run in. Each template
 * below names all three separately rather than trusting one clause to cover them.
 *
 * Where content is paid promotion, these templates require compliance with applicable disclosure
 * requirements and with the brand's own written disclosure guidelines. They do not attempt to state
 * what any particular requirement says — that is a question for counsel and for a brief that counsel
 * has reviewed.
 *
 * Every body uses visible [BRACKET] blanks. An unfilled blank stays on the page on purpose.
 * The renderer prepends the standard disclaimer; nothing here competes with it.
 */

export const MARKETING_TEMPLATES = [
  {
    id: "marketing-agency-agreement",
    docId: "marketing-agency-agreement",
    title: "MARKETING AGENCY AGREEMENT",
    version: "1.0",
    lastReviewed: "2026-08-25",

    founderGuide: {
      useThisWhen:
        "Before an agency starts work, and specifically before you grant anyone administrative access to an ad account, an analytics property, an email platform or your store. Access is a two-minute settings change to give and a genuinely hard thing to take back.",
      whyItMatters:
        "Two things go wrong, and neither is about performance. First, paying an invoice is not an assignment: without written transfer language the agency owns the concepts, the layouts and the edits it produced, and you hold an implied permission at best. Second, and far more common, the agency runs everything inside its own business manager — your ad account, your pixel, your custom audiences, your creative library and a year and a half of learning. You can rebuild creative. You cannot rebuild account history, and starting a new pixel from zero is a real cost paid in wasted media. Fix both before the first login is granted, because after that you are asking rather than requiring.",
      negotiateCarefully: [
        {
          point: "Every account is opened under your ownership, not theirs",
          detail:
            "The brand's own business manager owns the ad accounts, pixels, tags, audiences, catalogues, analytics properties and retail media accounts, and the agency is added as a user. This is the single highest-value clause in the document, it costs nothing at signing, and it is close to impossible to fix once a year of history sits under someone else's roof.",
        },
        {
          point: "Assignment of creative plus delivery of editable files",
          detail:
            "Take a present assignment of everything produced and require the layered, editable working files rather than exports. An agency that owns the winning creative can re-license it to you at renewal, and an agency that delivers only flattened files leaves you unable to make a cutdown, swap a pack shot after a label change or localise for a retailer.",
        },
        {
          point: "How media money moves, and what is added on top",
          detail:
            "Decide who is invoiced by the platforms and who carries the payment liability, then require written disclosure of any markup on pass-through production, influencer fees, stock and print, with a right to see the underlying invoices. A percentage-of-spend fee rewards spending more rather than spending well, so pair it with a floor, a ceiling or an outcome element.",
        },
        {
          point: "Approval workflow, especially for anything that reads as a claim",
          detail:
            "Product claims in an ad are the brand's exposure, not the agency's. Require every claim, benefit statement and comparison to be routed through the brand's review and to carry a documented approval before it goes live, and give the workflow a turnaround so approvals do not become the bottleneck the agency blames.",
        },
        {
          point: "Transition-out, written before you need it",
          detail:
            "Set out exactly what happens on termination: admin ownership transferred, agency users removed, working files and creative libraries delivered, audiences and reporting exported where the platform allows, credentials handed over — all within a stated period, and as an obligation that survives whatever the two of you are arguing about.",
        },
      ],
      redFlags: [
        "Ad accounts, pixels, analytics properties or email platforms held under the agency's ownership, with no transfer obligation.",
        "No assignment language anywhere, or a licence to the creative that ends with the retainer.",
        "Automatic renewal for a further term unless notice is given far in advance.",
        "Refusal to disclose markups on pass-through costs or to show third-party invoices.",
        "Working and editable files described as agency property and excluded from the deliverables.",
        "No stated transition-out obligation, or one that is conditional on all invoices being settled first.",
      ],
      timeToComplete: "30 minutes to fill · allow a week to negotiate access and transition terms",
    },

    placeholders: [
      { token: "[EFFECTIVE DATE]", label: "Effective date", example: "September 1, 2026" },
      { token: "[COMPANY NAME]", label: "Your legal entity name", brandKey: "legalEntityName", example: "Acme Beverage Co., LLC" },
      { token: "[COMPANY STATE]", label: "Your state of formation", brandKey: "stateOfFormation", example: "Delaware" },
      { token: "[COMPANY ENTITY TYPE]", label: "Your entity type", example: "limited liability company" },
      { token: "[COMPANY ADDRESS]", label: "Your address", brandKey: "principalAddress", example: "123 Main St, Austin, TX 78701" },
      { token: "[BRAND NAME]", label: "Your brand name", brandKey: "tradeName", example: "Acme" },
      { token: "[AGENCY NAME]", label: "Agency's legal name", example: "Northbound Growth Partners LLC" },
      { token: "[AGENCY STATE]", label: "Agency's state of formation", example: "New York" },
      { token: "[AGENCY ENTITY TYPE]", label: "Agency's entity type", example: "limited liability company" },
      { token: "[AGENCY ADDRESS]", label: "Agency's address", example: "22 Water St, Brooklyn, NY 11201" },
      { token: "[SERVICES SUMMARY]", label: "One line describing the services", example: "paid social and retail media management, creative production and reporting" },
      { token: "[SCOPE OF SERVICES]", label: "Scope of services (Schedule A)", example: "paid social buying, monthly creative batch, marketplace ads, weekly reporting" },
      { token: "[RETAINER FEE]", label: "Retainer or base fee", example: "$9,500 per month" },
      { token: "[MEDIA FEE BASIS]", label: "Any fee calculated on media spend", example: "8% of managed media above $50,000 per month, capped at $6,000" },
      { token: "[INVOICE FREQUENCY]", label: "How often the agency invoices", example: "monthly, in advance for the retainer and in arrears for media fees" },
      { token: "[PAYMENT PERIOD]", label: "Time to pay a correct invoice", example: "15 days from receipt" },
      { token: "[MEDIA BUDGET AUTHORISATION]", label: "How media budget is approved", example: "a written monthly budget, with any increase above it approved in writing" },
      { token: "[APPROVAL TURNAROUND]", label: "Time the Company has to approve work", example: "3 business days" },
      { token: "[REPORTING CADENCE]", label: "How often the agency reports", example: "weekly summary and a monthly review call" },
      { token: "[REPORTED METRICS]", label: "Metrics the report must contain", example: "spend, impressions, CTR, CPA, ROAS by campaign, and creative-level results" },
      { token: "[INITIAL TERM]", label: "Initial term", example: "6 months" },
      { token: "[TERMINATION NOTICE PERIOD]", label: "Notice to end the agreement", example: "30 days" },
      { token: "[TRANSITION PERIOD]", label: "Time to complete transition-out after termination", example: "15 days" },
      { token: "[CONFIDENTIALITY PERIOD]", label: "How long confidentiality runs afterwards", example: "3 years" },
      { token: "[EXCLUSIVITY CATEGORY]", label: "Competing category for conflict purposes", example: "ready-to-drink canned functional beverages sold in US grocery" },
      { token: "[BRAND PLATFORM ASSETS]", label: "Accounts and platform assets covered (Schedule B)", example: "Meta business manager, ad account, pixel, catalogue, GA4 property, Klaviyo, Amazon Ads" },
      { token: "[THIRD PARTY MATERIALS]", label: "Third-party stock, music and font licences (Schedule C)", example: "Stock music licence, held by Agency, no paid-media use" },
      { token: "[GOVERNING LAW STATE]", label: "Governing law state", example: "Delaware" },
      { token: "[VENUE]", label: "Venue for disputes", example: "Wilmington, Delaware" },
    ],

    body: `This Marketing Agency Agreement (this "Agreement") is entered into as of [EFFECTIVE DATE] (the
"Effective Date") by and between:

  [COMPANY NAME], a [COMPANY STATE] [COMPANY ENTITY TYPE], with a principal place of
  business at [COMPANY ADDRESS], which markets products under the brand [BRAND NAME]
  (the "Company"); and

  [AGENCY NAME], a [AGENCY STATE] [AGENCY ENTITY TYPE], with a principal place of
  business at [AGENCY ADDRESS] (the "Agency").

1. SERVICES AND SCOPE
The Company engages the Agency to provide [SERVICES SUMMARY] (the "Services"), as described in
Schedule A. The Agency shall perform the Services with the skill and care reasonably expected of a
professional agency, in accordance with the Company's written brand guidelines, disclosure
guidelines and approval workflow, and in accordance with the terms and policies of each advertising,
retail media, analytics and email platform used.

Work outside Schedule A is performed only where the Company approves it in writing in advance,
including the fee for it. Where Schedule A conflicts with this Agreement, this Agreement controls on
ownership, platform assets, confidentiality, approvals and transition, and Schedule A controls on
scope, fees and timing.

2. FEES
The Company shall pay the Agency [RETAINER FEE] for the Services, plus [MEDIA FEE BASIS] where a
fee is calculated on media spend. The Agency shall invoice [INVOICE FREQUENCY], and the Company
shall pay each correct and undisputed invoice within [PAYMENT PERIOD]. Each invoice shall separate
agency fees, pass-through costs and media spend.

3. MEDIA SPEND AND PASS-THROUGH COSTS
Media budget is set and changed only in accordance with [MEDIA BUDGET AUTHORISATION]. The Agency
shall not commit the Company to spend beyond the authorised budget, and any excess committed without
written authorisation is the Agency's own cost.

Wherever a platform permits it, the Company's own payment instrument is attached to the Company's
own account and the platform invoices the Company directly. Where the Agency pays a platform or a
supplier on the Company's behalf, it does so as the Company's disclosed agent, holds the funds for
that purpose only, and shall provide the underlying platform or supplier invoice on request.

The Agency shall disclose in writing any markup, commission, rebate, incentive, agency credit or
other benefit it receives or applies in connection with any pass-through cost, including production,
influencer and creator fees, stock, print, freight and platform rebates, and shall not apply any
markup the Company has not approved in writing. The Company may audit pass-through costs and the
Agency shall provide supporting third-party invoices on request.

4. PLATFORM ACCOUNTS, DATA AND ADMINISTRATIVE ACCESS
This Section applies to every advertising, commerce, analytics, email, messaging and retail media
asset used for the Company, whether it existed before the Effective Date or is created during the
term. Schedule B lists them as at the Effective Date, and the Agency shall keep that list current.

Ownership. The Company owns, and at all times retains ownership of and administrative access to,
every business manager, advertising account, ad platform seat, tracking pixel, conversion API
configuration, tag and tag-manager container, custom, lookalike and retargeting audience, customer
list uploaded to any platform, product catalogue and feed, analytics property and data stream,
email, SMS and messaging platform account and its subscriber list, social account, handle and page,
retailer and marketplace advertising account, storefront and seller-central style account, domain,
subdomain, landing page builder account, and reporting dashboard used for the Company (together the
"Brand Platform Assets"). The Brand Platform Assets, and all data in them, are the Company's
property and its Confidential Information.

Creation. The Agency shall create every new Brand Platform Asset inside the Company's own business
manager, platform account or organisation, under the Company's ownership, using an administrator
identity the Company controls. The Agency is added as a user or partner with the access level it
needs. The Agency shall not create, hold or operate any Brand Platform Asset under its own
ownership, its own business manager, a shared agency account, a sub-account of an agency account, or
the personal account of any individual.

Where a Brand Platform Asset already sits under the Agency's ownership at the Effective Date, or is
found to during the term, the Agency shall transfer ownership of it to the Company promptly and at
its own cost, and shall assist with any platform process the transfer requires.

Continuity. The Agency shall maintain the Company as an administrator at all times and shall not
remove, downgrade, suspend, restrict or transfer the Company's access, or any Brand Platform Asset,
for any reason, including a dispute about fees. The Agency shall not merge any Brand Platform Asset
into another entity's structure, rename it in a way that obscures the Company's ownership, or link
it to another client's assets. Any unpaid amount is a debt owed to the Agency and is not a basis for
withholding access.

Data. The Agency shall use data in the Brand Platform Assets only to perform the Services, shall not
use it for any other client or for its own purposes, shall not combine it with another client's
data, and shall not export or retain it after the term except as Section 11 requires. The Agency
shall handle customer data in accordance with the Company's written instructions and applicable
platform terms, and shall notify the Company promptly of any unauthorised access to it.

5. OWNERSHIP OF CREATIVE AND DELIVERY OF WORKING FILES
"Work Product" means everything the Agency conceives, creates, designs, writes, photographs, films,
records, codes or produces for the Company under this Agreement, including concepts, scripts,
storyboards, ad creative, static and video assets, landing pages, email templates, copy, reports and
all drafts, variants, rejected routes and working files.

The Agency hereby irrevocably assigns to the Company all right, title and interest worldwide in and
to the Work Product, including all copyrights and other intellectual property rights in it. This is
a present assignment, effective as each item is created, and it is not a promise to assign in the
future. Ownership is not conditional on payment; any unpaid amount is a debt owed to the Agency and
is not a basis for withholding, revoking or re-taking ownership of, or access to, the Work Product.
To the extent permitted, the Agency waives, and agrees not to assert against the Company or those
claiming through it, any right of attribution or integrity or any similar right, however described,
in the Work Product, and shall secure the same waiver in writing from every person who contributes
to it.

Working files. Delivery of exports alone is not delivery. On request, and in any event on
termination, the Agency shall deliver the Work Product in native editable form, including layered
design files with live type and linked assets, video and audio project files with the media they
reference, motion and template project files, raw camera files and outtakes, fonts used or a written
list identifying each font and its licence, landing page and email templates in editable form, and
the full creative library including every variant that has run.

The Agency shall not engage any subcontractor for creative production without the Company's prior
written consent, and before an approved subcontractor begins shall bind it in writing to
confidentiality, assignment and waiver obligations at least as protective as this Section.

6. THIRD-PARTY LICENCES AND STOCK DISCLOSURE
Schedule C lists every third-party font, stock image, stock footage clip, music track, sound effect,
voice, illustration, template, plug-in and creator-supplied asset embedded in or required to use the
Work Product, together with the licence covering it, who holds it, its term and its territory.

The Agency represents that each listed licence permits the Company's intended use, which includes
paid advertising on any platform, organic and owned channels, the Company's website, retailer and
marketplace listings, trade and shopper materials, email, and out-of-home, and permits the Company to
transfer or sublicense those uses to its distributors, retailers, licensees and successors. Where a
licence is time-limited, the Agency shall state the expiry in Schedule C and shall notify the
Company in writing before any asset in a live campaign reaches it. Where a licence must be purchased
in the Company's name, the Agency shall say so before the asset is used. The Agency shall not
incorporate any third-party material not listed on Schedule C without the Company's prior written
consent.

7. APPROVAL WORKFLOW
Nothing is published, launched, sent or set live without the Company's written approval, given
through the workflow in Schedule A. The Company shall approve or comment within
[APPROVAL TURNAROUND] of receiving a complete submission; if it does not respond within that period
the Agency shall follow up and shall not treat silence as approval.

Claims. Every claim, benefit statement, ingredient or nutrition reference, comparison, testimonial,
before-and-after framing and certification or seal usage requires the Company's specific written
approval before use, and the Agency shall use only language the Company has approved in writing. The
Agency shall not add, amend, paraphrase or extrapolate from approved claim language, including in
headlines, captions, alt text, ad variations, automatically generated variants and platform-generated
copy suggestions.

Disclosure. Where the Services involve paid promotion, sponsored content, endorsements, affiliate
links or content produced by creators, the Agency shall comply with applicable disclosure
requirements and with the Company's written disclosure guidelines, shall ensure that every creator
and publisher it engages is contractually required to do the same, and shall keep records of what
was disclosed and how. The Agency shall raise with the Company anything it is unsure about before
publication rather than after, and the parties acknowledge that what any particular requirement
demands is a question for the Company's counsel.

The Agency shall retain, for the term and for [CONFIDENTIALITY PERIOD] afterwards, a record of each
approval, the version approved and the date, and shall provide it on request.

8. PERFORMANCE REPORTING
The Agency shall report [REPORTING CADENCE], covering at least [REPORTED METRICS], broken out by
campaign, channel and creative, and shall include media spend reconciled to platform invoices and
pass-through costs itemised. Reports are provided in a format the Company can retain and reuse, and
not only as a slide or a dashboard the Agency controls. The Agency shall grant the Company
read-access to the live reporting for every Brand Platform Asset, and shall notify the Company
promptly of any material change in performance, any platform account restriction, any disapproved
asset and any suspected policy breach.

9. CONFIDENTIALITY
"Confidential Information" means the Company's non-public information in any form, including:
formulations, ingredient sourcing and landed cost; co-manufacturer, supplier and freight terms;
margin structure, pricing architecture, promotional calendars and trade spend; retailer and
distributor terms, planograms and buyer feedback; customer lists, subscriber lists and first-party
data; sales, forecast and financial information; unreleased products, packaging and launch plans;
and the Brand Platform Assets and their data.

The Agency shall hold Confidential Information in confidence, shall not disclose it without the
Company's prior written consent, and shall not use it for any purpose other than performing the
Services or for the benefit of any other client. These obligations continue for
[CONFIDENTIALITY PERIOD] after termination and, for anything the Company treats as a trade secret,
for as long as it remains one. The usual exceptions apply to information already known without a
duty of confidence, information that becomes public through no act of the Agency, information
rightfully received from a third party free of any duty of confidence, and information independently
developed without reference to the Company's.

10. CONFLICTS, TERM AND TERMINATION
Conflicts. The Agency shall notify the Company in writing before accepting any engagement with a
business in [EXCLUSIVITY CATEGORY], and shall not use the Company's Confidential Information, Work
Product or Brand Platform Asset data in any such engagement. The Company may end this Agreement on
written notice if the Agency accepts such an engagement.

Term. This Agreement begins on the Effective Date and continues for [INITIAL TERM], after which it
continues until ended under this Section. It does not renew automatically for a further fixed term.

Termination. Either party may end this Agreement for any reason on [TERMINATION NOTICE PERIOD]
written notice. Either party may end it immediately on written notice if the other materially
breaches and does not cure within [TERMINATION NOTICE PERIOD] of written notice describing the
breach. The Company may end it immediately if the Agency breaches Section 4 or Section 7. The
Company may direct the Agency to stop work at any time, and fees then run only to the end of the
notice period.

On termination the Company shall pay for Services properly performed and media properly committed to
the termination date. Neither party owes the other the balance of an unperformed term. Sections 4,
5, 6, 9, 11, 12, 13 and 14 survive termination.

11. TRANSITION OUT
The obligations in this Section arise on notice of termination, are not conditional on payment of
any disputed amount, and are performed at the Agency's own cost within [TRANSITION PERIOD] of the
termination date, or sooner where the Company reasonably requires it to avoid disruption to live
campaigns.

The Agency shall:
(a) confirm the Company holds full administrative ownership of every Brand Platform Asset, and
    complete any platform transfer process needed to achieve that;
(b) remove its own users, partner links, agency access, integrations, tokens and API connections,
    and confirm removal in writing;
(c) deliver all Work Product in the native editable form required by Section 5, together with the
    complete creative library, naming conventions and asset index;
(d) deliver every credential, licence key and account record it holds for the Company, and any
    domain, subdomain or landing page it registered for the Company;
(e) export and deliver, in a reusable format and to the extent the platform permits it, audience
    definitions, subscriber and customer lists, campaign structures, historical performance data,
    reporting and tracking configurations;
(f) deliver copies of every third-party licence and creator or influencer agreement relating to
    assets in use, and assign or transfer them to the Company where their terms permit;
(g) deliver a written handover note covering live campaigns, scheduled spend, pending platform
    reviews, outstanding pass-through costs and anything time-sensitive; and
(h) delete the Company's data from its own systems after handover, other than one archival copy for
    its own compliance records, which remains subject to Section 9.

The Agency shall not pause, delete, archive or restrict any campaign, audience, asset or account
during the transition except at the Company's written direction, and shall not use the transition as
leverage in any fee dispute. The Company may withhold a reasonable portion of final fees until the
transition obligations are complete.

12. WARRANTIES
The Agency represents and warrants that: it has full authority to enter into this Agreement; it will
perform the Services in accordance with applicable platform terms and with the Company's written
brand, claim and disclosure guidelines; the Work Product is original except for the material listed
on Schedule C; the Work Product does not, to the best of the Agency's knowledge, infringe or
misappropriate the rights of any third party; and it holds the rights, waivers and releases it is
required to secure under Sections 5 and 6.

13. GOVERNING LAW AND VENUE
This Agreement is governed by the laws of the State of [GOVERNING LAW STATE], without regard to its
conflict of laws principles. The parties consent to venue in [VENUE].

14. GENERAL
The Agency is an independent contractor and not the Company's employee, partner or joint venturer,
and has no authority to bind the Company beyond the authority expressly given in this Agreement.
This Agreement, together with its Schedules, is the entire agreement between the parties regarding
its subject matter and supersedes all prior discussions and proposals. Where it conflicts with a
statement of work, insertion order, proposal or invoice, this Agreement controls on ownership,
platform assets, third-party material, approvals and transition. It may be amended only in a writing
signed by both parties. The Agency may not assign this Agreement without the Company's prior written
consent; the Company may assign it to a successor in connection with a merger or a sale of
substantially all of its assets. If any provision is held unenforceable, the remainder continues in
effect. This Agreement may be signed in counterparts, including by electronic signature, each of
which is an original.

IN WITNESS WHEREOF, the parties have executed this Agreement as of the Effective Date.


[COMPANY NAME]                            [AGENCY NAME]


By: _______________________________       By: _______________________________

Name: _____________________________       Name: _____________________________

Title: ____________________________       Title: ____________________________

Date: _____________________________       Date: _____________________________


─────────────────────────────────────────────────────────────────────────────
SCHEDULE A — SCOPE OF SERVICES AND APPROVAL WORKFLOW
─────────────────────────────────────────────────────────────────────────────
Describe the Services, the deliverable cadence, the named team, and the approval workflow: who at
the Company approves what, in what tool, and what must go to claim review before it can be
submitted.

Scope of services:

[SCOPE OF SERVICES]

_____________________________________________________________________________

Approval workflow and named approvers:

_____________________________________________________________________________

Agency initials: __________          Company initials: __________


─────────────────────────────────────────────────────────────────────────────
SCHEDULE B — BRAND PLATFORM ASSETS
─────────────────────────────────────────────────────────────────────────────
Every business manager, ad account, pixel, tag, audience, catalogue, analytics property, email and
messaging platform, social handle, retail media account, storefront, domain and dashboard used for
the Company. For each one: the platform, the identifier, who created it, who owns it today and who
holds administrator access. Anything owned by the Agency today is transferred under Section 4.

[BRAND PLATFORM ASSETS]

_____________________________________________________________________________

_____________________________________________________________________________

Agency initials: __________          Company initials: __________


─────────────────────────────────────────────────────────────────────────────
SCHEDULE C — THIRD-PARTY MATERIAL AND LICENCES
─────────────────────────────────────────────────────────────────────────────
Every third-party font, stock image, footage clip, music track, sound effect, voice, illustration,
template or creator-supplied asset in the work. For each one: what it is, the licence, who holds it,
the term, the territory, and whether it reaches paid advertising, retailer and marketplace listings
and out-of-home. Write "None" if there is none.

[THIRD PARTY MATERIALS]

_____________________________________________________________________________

_____________________________________________________________________________

Agency initials: __________          Company initials: __________
`,
  },

  {
    id: "influencer-agreement",
    docId: "influencer-agreement",
    title: "INFLUENCER AGREEMENT",
    version: "1.0",
    lastReviewed: "2026-08-25",

    founderGuide: {
      useThisWhen:
        "Before any paid or gifted collaboration where a creator will post about your product — including the seeding programme you think is too small to paper, because a gifted post with no agreement gives you rights to nothing.",
      whyItMatters:
        "The expensive gap in CPG influencer deals is that brands buy a post rather than an asset. Organic posting, the brand's own paid use of the content, and whitelisting from the creator's handle are three separate grants with three separate durations, and a standard creator quote covers only the first. So the sequence keeps repeating: run a batch of creators, find the one video that actually converts, discover you have no right to put budget behind it, and negotiate for the asset at the exact moment its value is proven and visible to both sides. A brand that finds a winning creative and cannot scale it as paid media has bought nothing. Price all three at the start, when this creator's content is worth the same as everyone else's.",
      negotiateCarefully: [
        {
          point: "Three grants, three durations, three territories",
          detail:
            "Organic use on the creator's channels, the brand's paid use of the content, and whitelisting are separate permissions and should be separate lines in the deal. Write each with its own term and territory. Anything you leave silent is a grant you did not buy, and buying it later is priced against your own performance data.",
        },
        {
          point: "Whitelisting needs the access, not just the sentence",
          detail:
            "Running an ad from the creator's own handle usually performs differently from running the same content from yours, and it requires platform-level permission plus a partnership or creator-account link the creator has to actually configure. A clause saying whitelisting is permitted, with no obligation to set it up and keep it live, buys you an argument rather than a capability.",
        },
        {
          point: "Minimum live period, and notice before deletion",
          detail:
            "A creator free to delete the post can end a campaign your paid spend is anchored to without breaching anything, and can take the social proof with it. Set a minimum live period, require notice before any deletion or archive, and confirm that your paid usage survives the organic post coming down.",
        },
        {
          point: "Claims, not vibes",
          detail:
            "The brand carries the exposure for how a creator describes a functional beverage or supplement, and personal-results storytelling is exactly what performs. Limit the creator to language you have approved in writing, supply a do-not-say list, and have counsel review the approved claim set before it goes to a single creator — not after a roster of them has already filmed.",
        },
        {
          point: "Exclusivity has two dimensions and founders only price one",
          detail:
            "There is the window during which the creator may not post for a competitor, and there is what counts as a competitor. Written narrowly, a directly competing product appears a week later; written broadly, you price yourself out of creators who work across the whole category. Define both, in writing, and pay for the version you actually need.",
        },
      ],
      redFlags: [
        "A fee that covers posting only, with usage rights described as something to discuss later.",
        "Paid usage that expires before the campaign it is funding, with no agreed extension price.",
        "Whitelisting granted in words with no obligation to configure or maintain the platform access.",
        "No minimum live period and no notice requirement before deletion or archiving.",
        "No disclosure obligation, or no approval right over how the product is described.",
        "Management or network terms referenced but not attached, so the actual grant sits in a document you have not seen.",
      ],
      timeToComplete: "25 minutes to fill · negotiate the three grants before agreeing the fee",
    },

    placeholders: [
      { token: "[EFFECTIVE DATE]", label: "Effective date", example: "September 1, 2026" },
      { token: "[COMPANY NAME]", label: "Your legal entity name", brandKey: "legalEntityName", example: "Acme Beverage Co., LLC" },
      { token: "[COMPANY STATE]", label: "Your state of formation", brandKey: "stateOfFormation", example: "Delaware" },
      { token: "[COMPANY ENTITY TYPE]", label: "Your entity type", example: "limited liability company" },
      { token: "[COMPANY ADDRESS]", label: "Your address", brandKey: "principalAddress", example: "123 Main St, Austin, TX 78701" },
      { token: "[BRAND NAME]", label: "Your brand name", brandKey: "tradeName", example: "Acme" },
      { token: "[CREATOR NAME]", label: "Creator's legal name (and management, if any)", example: "Jordan Ellis, f/s/o Ellis Media LLC" },
      { token: "[CREATOR ADDRESS]", label: "Creator's address", example: "1207 Larkin St, San Francisco, CA 94109" },
      { token: "[CREATOR HANDLES]", label: "The creator's handles and platforms", example: "@jordaneats on Instagram and TikTok" },
      { token: "[DELIVERABLES BY PLATFORM]", label: "Deliverables by platform and format (Schedule A)", example: "1 Instagram Reel 30-45s, 3 Stories frames with link, 1 TikTok 45-60s" },
      { token: "[POSTING SCHEDULE]", label: "Posting dates and times", example: "Reel on Oct 8, Stories same day, TikTok on Oct 11, all after 5pm local" },
      { token: "[MINIMUM LIVE PERIOD]", label: "How long the organic post must stay live", example: "12 months" },
      { token: "[ORGANIC TERM]", label: "Duration of the organic use grant", example: "perpetual for content that remains posted" },
      { token: "[ORGANIC TERRITORY]", label: "Territory of the organic use grant", example: "worldwide" },
      { token: "[PAID USAGE TERM]", label: "Duration of the brand's paid advertising rights", example: "perpetual, or 24 months from first delivery" },
      { token: "[PAID USAGE TERRITORY]", label: "Territory of the paid advertising rights", example: "United States and Canada" },
      { token: "[WHITELISTING TERM]", label: "Duration of the whitelisting rights", example: "12 months from first delivery" },
      { token: "[WHITELISTING TERRITORY]", label: "Territory of the whitelisting rights", example: "United States" },
      { token: "[USAGE EXTENSION FEE]", label: "Agreed price to extend paid usage or whitelisting", example: "$1,500 per additional 6 months per asset" },
      { token: "[EXCLUSIVITY WINDOW]", label: "How long the creator may not post for a competitor", example: "90 days from the first posting date" },
      { token: "[EXCLUSIVITY CATEGORY]", label: "What counts as a competing product", example: "canned energy and functional beverages, and powdered energy mixes" },
      { token: "[REVISION ROUNDS]", label: "Revision rounds included in the fee", example: "two rounds per deliverable" },
      { token: "[APPROVAL TURNAROUND]", label: "Time the Company has to approve a draft", example: "3 business days" },
      { token: "[FEE]", label: "Total fee and what it covers", example: "$4,000, allocated $2,000 organic / $1,500 paid usage / $500 whitelisting" },
      { token: "[PAYMENT SCHEDULE]", label: "When each portion is paid", example: "half on signature, half within 15 days of the final post going live" },
      { token: "[PRODUCT PROVIDED]", label: "Product and materials the Company supplies", example: "two 12-can cases of the current pack, plus the brand kit" },
      { token: "[CANCELLATION FEE]", label: "What is payable if the Company cancels before filming", example: "25% of the fee if cancelled after the brief is accepted but before filming" },
      { token: "[GOVERNING LAW STATE]", label: "Governing law state", example: "Delaware" },
      { token: "[VENUE]", label: "Venue for disputes", example: "Wilmington, Delaware" },
    ],

    body: `This Influencer Agreement (this "Agreement") is entered into as of [EFFECTIVE DATE] (the "Effective
Date") by and between:

  [COMPANY NAME], a [COMPANY STATE] [COMPANY ENTITY TYPE], with a principal place of
  business at [COMPANY ADDRESS], which markets products under the brand [BRAND NAME]
  (the "Company"); and

  [CREATOR NAME], with an address at [CREATOR ADDRESS] (the "Creator"), who posts under
  [CREATOR HANDLES] (the "Channels").

1. DELIVERABLES AND POSTING SCHEDULE
The Creator shall produce and post the content described as [DELIVERABLES BY PLATFORM] in Schedule
A (the "Content"), on the Channels, according to [POSTING SCHEDULE]. Each item shall meet the
platform, format, length, aspect ratio and captioning requirements in Schedule A.

The Creator shall keep each posted item live and publicly visible on the Channels for at least
[MINIMUM LIVE PERIOD] from the date it is posted, shall not archive, restrict, hide or delete it
during that period, and shall give the Company written notice before removing it at any time
afterwards. If an item is removed early other than at the Company's request or for a platform
reason outside the Creator's control, the Creator shall repost it or the parties shall agree a
proportionate fee adjustment.

2. RAW AND SOURCE FILE DELIVERY
Within the timing stated in Schedule A, and in any event before the final payment is due, the
Creator shall deliver to the Company: the final edited files in the highest resolution available;
all raw and unedited footage, takes and stills captured for the Content, including material not used
in the edit; any project or edit files, captions and subtitle files; and a list of any music, sound
effect, font or stock asset used. Delivery is by a method the Company can retain, not by a link that
expires. The raw material is delivered so the Company can exercise the rights in Sections 3 and 4.

3. GRANT ONE — ORGANIC USE ON THE CREATOR'S CHANNELS
The Creator shall post the Content on the Channels as its own organic content, and grants the
Company the right to view, share, repost, duet, stitch, save and reference that organic content, and
to link to it, on the Company's own owned and earned channels.

Term: [ORGANIC TERM]. Territory: [ORGANIC TERRITORY].

This grant covers organic posting and organic amplification only. It does not permit the Company to
run the Content as advertising or to spend media against it. That right is granted, if at all, only
by Section 4.

4. GRANT TWO — THE COMPANY'S PAID ADVERTISING RIGHTS
The Creator grants the Company a licence to use, reproduce, edit, re-cut, subtitle, translate,
overlay, excerpt, combine with other material and create derivative works from the Content and the
raw material, and to publish and distribute it as the Company's own advertising, from the Company's
own accounts and handles, across paid social, video, display, search, retail media, marketplace and
connected-television placements, and in the Company's owned channels, website, email and organic
social.

Term: [PAID USAGE TERM]. Territory: [PAID USAGE TERRITORY].

This grant is separate from Section 3, is not affected by the Content being removed from the
Channels, and includes the right to sublicense the same uses to the Company's agencies, distributors
and retailers for the purpose of advertising the Company's products. The Company may run the
Content with its own creative treatments, hooks and end cards, provided it does not alter approved
claim language contrary to Section 8.

5. GRANT THREE — WHITELISTING AND ADS FROM THE CREATOR'S HANDLE
The Creator grants the Company the right to run advertising that appears as published by, or in
partnership with, the Creator's own handle, including partnership, branded-content, spark-style and
equivalent ad formats.

Term: [WHITELISTING TERM]. Territory: [WHITELISTING TERRITORY].

To make that right usable, the Creator shall, within the timing in Schedule A: grant the Company the
platform-level permission each relevant platform requires, including advertiser, partnership or
branded-content access to the Creator's account; issue and maintain any code, token or authorisation
the platform requires for the Company to run ads from the handle; keep that permission and any
required account setting active for the whole whitelisting term; and cooperate promptly with any
platform process needed to establish or restore it. The Creator shall not revoke the permission
during the term, and shall give the Company written notice before making any change to the account
that would affect it.

The Company shall not, using this access, post organic content from the Creator's handle, respond to
comments as the Creator, or run advertising for any brand other than [BRAND NAME].

6. EXTENSIONS AND WHAT IS NOT GRANTED
Where the Company wishes to extend the term or territory of the grant in Section 4 or Section 5, the
fee is [USAGE EXTENSION FEE], and the Creator shall grant the extension on written notice from the
Company and payment of that fee. Any use outside the grants above — including use on product
packaging, on retail packaging inserts, on out-of-home media, or as the basis of a separate campaign
shot from the raw material for a different product line — requires a separate written agreement.

7. EXCLUSIVITY
For [EXCLUSIVITY WINDOW], the Creator shall not create, post or appear in any paid, gifted,
affiliate or sponsored content for any product within [EXCLUSIVITY CATEGORY], and shall not permit
its likeness or Channels to be used to promote such a product. The Creator shall not post any
content for a competing product immediately before or after a posting date in Schedule A. The
Creator shall disclose to the Company, before signing, any existing commitment within
[EXCLUSIVITY CATEGORY] and the date it ends. This Section does not restrict the Creator from
purchasing or personally using any product.

8. APPROVALS, CLAIMS AND PROHIBITED CONTENT
Approvals. The Creator shall submit each item to the Company for written approval before posting,
in the form and by the date stated in Schedule A. The fee includes [REVISION ROUNDS]. The Company
shall approve or comment within [APPROVAL TURNAROUND] of receiving a complete submission. Nothing is
posted without written approval, and the Creator shall not edit an approved item, or change its
caption, before or after posting without the Company's written approval.

Claims. The Creator shall describe the product only using language the Company has approved in
writing, and shall follow the written brief, including its required language and its do-not-say
list. The Creator shall not state or imply that the product treats, prevents, cures or reduces the
risk of any condition; shall not make any comparison to a named competitor; shall not present any
personal-results, before-and-after, weight, body-composition, performance or health outcome unless
the Company has approved that specific framing in writing; shall not state that the product is
suitable for any allergen, dietary or medical requirement beyond what the approved language says;
and shall not add ingredient, sourcing, certification or sustainability claims of its own. If the
Creator wants to say something the approved language does not cover, it shall ask first.

Prohibited content. The Creator shall not depict or associate the product with: consumption by
anyone under the age the brief specifies; excessive or unsafe consumption; alcohol, tobacco, vaping
or controlled substances; driving or operating machinery while consuming; medical, clinical or
professional-endorsement framing; sexually explicit, violent, discriminatory, harassing or hateful
material; content that disparages any retailer, distributor or competitor; or any competing product
visible in frame. The Creator shall not name a retailer as stocking the product unless the Company
confirms in writing that it is on shelf there.

Disclosure. The Creator shall clearly and conspicuously disclose the paid or gifted relationship
with the Company in every item of Content, in the content itself as well as in any platform
disclosure tool, in accordance with applicable disclosure requirements, the platform's own rules,
and the Company's written disclosure guidelines. Disclosure shall not be removed or obscured for the
life of the post, and shall be included in any repost or reshare. The Company's disclosure
guidelines are supplied with the brief; the Creator shall raise any question about them before
posting rather than after, and the parties acknowledge that what any particular requirement demands
is a question for the Company's counsel.

9. FEES, PRODUCT AND PAYMENT
The Company shall pay the Creator [FEE], on the terms set out in [PAYMENT SCHEDULE]. Schedule A
allocates the fee between the grants in Sections 3, 4 and 5 so the parties can price an extension or
a reduction in scope. Payment is conditional on the Content being posted as approved, the disclosure
in Section 8 being present, the raw material in Section 2 having been delivered, and, where
whitelisting is included, the access in Section 5 being active.

The Company shall supply [PRODUCT PROVIDED] at its own cost. Product supplied is for the Creator's
use in producing the Content and is not for resale. The Creator is responsible for its own taxes on
the fee and on the value of any product received, and for any obligation to its management or
network.

10. LIKENESS AND CREDIT
The Creator grants the Company the right to use its name, handle, voice, image, likeness and
biographical description as they appear in or relate to the Content, for the same terms and
territories as the grants in Sections 3, 4 and 5, and to identify the Creator as a partner of the
brand in connection with the Content. The Company is not required to credit the Creator beyond any
disclosure required by Section 8. The Creator confirms it has obtained a written release, on terms
at least as broad as this Section, from every other identifiable person appearing in the Content,
and shall deliver copies with the raw material.

11. CANCELLATION
The Company may cancel any deliverable or the whole engagement on written notice before posting. If
it cancels after the brief has been accepted but before filming, [CANCELLATION FEE] is payable. If
it cancels after filming but before posting, the Company shall pay the portion of the fee allocated
in Schedule A to the grants it retains, and the Creator shall deliver the Content and raw material
for those grants. If the Creator cancels, or fails to post by the dates in Schedule A other than for
a reason outside its control, it shall refund amounts already paid for undelivered items.

12. CONDUCT AND TERMINATION
Either party may end this Agreement immediately on written notice if the other materially breaches
it. The Company may additionally end this Agreement, and require the Creator to remove the Content
from the Channels, if the Creator engages in conduct, or makes a public statement, that is reasonably
likely to bring the Company or the brand into disrepute or to associate it with material of the kind
listed in Section 8. In that event the Company may cease using the Content, and no further fee is
payable for deliverables not yet posted; amounts already paid for grants already exercised are not
refundable.

Termination does not end grants already made and paid for under Sections 4 and 5 for the balance of
their terms, unless the Company elects in writing to stop using the Content. Sections 2, 4, 5, 8, 10,
13, 14 and 15 survive termination.

13. WARRANTIES AND CONFIDENTIALITY
The Creator represents and warrants that: it has full authority to enter into this Agreement and,
where management or a network represents it, that this Agreement is binding notwithstanding any
other terms, and it shall provide a copy of any terms its management or network seeks to apply; the
Content is original to the Creator except for material disclosed under Section 2; the Content and
its use as permitted by this Agreement do not, to the best of the Creator's knowledge, infringe or
misappropriate the rights of any third party; it has the releases required by Section 10; it has not
purchased followers, engagement or views on the Channels; and the audience data it has supplied is
accurate to the best of its knowledge.

The Creator shall keep confidential the terms of this Agreement, the brief, any unreleased product,
packaging, pricing, launch date or campaign plan, and any performance data the Company shares, and
shall not post about the collaboration before the first posting date without the Company's written
consent.

14. GOVERNING LAW AND VENUE
This Agreement is governed by the laws of the State of [GOVERNING LAW STATE], without regard to its
conflict of laws principles. The parties consent to venue in [VENUE].

15. GENERAL
The Creator is an independent contractor and not the Company's employee, partner or agent, and has
no authority to bind the Company or to make any commitment on its behalf. This Agreement, together
with Schedule A, is the entire agreement between the parties regarding its subject matter and
supersedes all prior discussions, rate cards, media kits and platform marketplace terms. It may be
amended only in a writing signed by both parties. The Creator may not assign this Agreement; the
Company may assign it to a successor in connection with a merger or a sale of substantially all of
its assets. If any provision is held unenforceable, the remainder continues in effect. This
Agreement may be signed in counterparts, including by electronic signature, each of which is an
original.

IN WITNESS WHEREOF, the parties have executed this Agreement as of the Effective Date.


[COMPANY NAME]                            [CREATOR NAME]


By: _______________________________       By: _______________________________

Name: _____________________________       Name: _____________________________

Title: ____________________________       Title: ____________________________

Date: _____________________________       Date: _____________________________


─────────────────────────────────────────────────────────────────────────────
SCHEDULE A — DELIVERABLES, SCHEDULE AND FEE ALLOCATION
─────────────────────────────────────────────────────────────────────────────
Deliverables by platform and format:

[DELIVERABLES BY PLATFORM]

_____________________________________________________________________________

Posting schedule, submission dates and raw file delivery date:

[POSTING SCHEDULE]

_____________________________________________________________________________

Fee allocation — write a number against each grant so an extension or a cutback can be priced:

  Organic use on the Channels (Section 3):        ____________________________

  Company paid advertising rights (Section 4):    ____________________________

  Whitelisting from the Creator's handle (Sec 5): ____________________________

Whitelisting access to be granted by (date):  _______________________________

Attach: the written brief, the approved claim language and the do-not-say list.

Creator initials: __________          Company initials: __________
`,
  },

  {
    id: "ugc-creator-agreement",
    docId: "ugc-creator-agreement",
    title: "UGC CREATOR AGREEMENT",
    version: "1.0",
    lastReviewed: "2026-08-25",

    founderGuide: {
      useThisWhen:
        "You are commissioning content that looks like a real customer made it, so the brand can run it as advertising. The creator films to your brief and delivers files; in most of these deals they never post anything.",
      whyItMatters:
        "This is the clearest case in marketing where the brand should be buying an asset rather than a post, and it is still where the licence gets left at twelve months because that is what the rate card said. Then the ad works. It becomes the account's top performer, the media plan is built on it, and the expiry date arrives with the creator holding all the leverage. The quieter failure is the raw material: without the unedited takes you cannot cut a new hook, swap a pack shot after a label change or make a shorter version for a new placement, so a working ad decays and you cannot refresh it. Buy it once, buy it outright, and get the raws.",
      negotiateCarefully: [
        {
          point: "Assignment, or a licence that behaves like one",
          detail:
            "Take a present assignment of the content. If the creator will not assign, take a perpetual, worldwide, irrevocable, transferable and sublicensable licence across every channel you might ever use, and treat any shorter term as a discount you consciously chose rather than a default you accepted.",
        },
        {
          point: "Raw and unedited files as a named deliverable",
          detail:
            "Every take, including the ones that did not make the edit, plus stills, project files and captions. This is what lets you make new hooks, re-cut for a new placement and refresh the ad when performance decays. Tie the final payment to delivery of the raw package, because after payment it stops arriving.",
        },
        {
          point: "Price packaging separately and deliberately",
          detail:
            "Putting a creator's face or content on the pack is a different deal from running it as an ad — it reaches shelf inside printed inventory you cannot recall, and it outlives any campaign. Keep it as a separately priced option in the same document so you can exercise it later at a price agreed today.",
        },
        {
          point: "The likeness grant has to be as wide as the content grant",
          detail:
            "The creator is on camera. If the content licence is perpetual and all-media but the appearance release is scoped to social for a year, the narrower grant is the one that governs. Match them, and get releases from anyone else who appears, including children and other adults in frame.",
        },
        {
          point: "Exclusivity, and what the creator may film next",
          detail:
            "Without it, the creator can shoot a visually identical piece for a competitor in the same feed, from the same kitchen, in the same week. Agree a defined category and a defined window, and separately bar reuse of your footage, your script and your approved language for anyone else, permanently.",
        },
      ],
      redFlags: [
        "A term-limited licence on content bought specifically to run as paid advertising.",
        "No right to edit, re-cut, subtitle or create derivative versions.",
        "Raw and unedited footage withheld, or offered as a paid extra after delivery.",
        "An appearance release narrower in channel, term or territory than the content grant.",
        "No brief and no approval step on a product with functional or health-adjacent positioning.",
        "Silence on whether the creator may produce similar content for competing brands.",
      ],
      timeToComplete: "20 minutes to fill · same-day to sign",
    },

    placeholders: [
      { token: "[EFFECTIVE DATE]", label: "Effective date", example: "September 1, 2026" },
      { token: "[COMPANY NAME]", label: "Your legal entity name", brandKey: "legalEntityName", example: "Acme Beverage Co., LLC" },
      { token: "[COMPANY STATE]", label: "Your state of formation", brandKey: "stateOfFormation", example: "Delaware" },
      { token: "[COMPANY ENTITY TYPE]", label: "Your entity type", example: "limited liability company" },
      { token: "[COMPANY ADDRESS]", label: "Your address", brandKey: "principalAddress", example: "123 Main St, Austin, TX 78701" },
      { token: "[BRAND NAME]", label: "Your brand name", brandKey: "tradeName", example: "Acme" },
      { token: "[CREATOR NAME]", label: "Creator's legal name", example: "Sam Okafor" },
      { token: "[CREATOR ADDRESS]", label: "Creator's address", example: "44 Pine Ridge Rd, Denver, CO 80209" },
      { token: "[CONTENT DELIVERABLES]", label: "Content deliverables (Schedule A)", example: "3 vertical videos 30-45s, 2 hook variants each, 6 lifestyle stills" },
      { token: "[TECHNICAL SPEC]", label: "Formats, aspect ratios and resolution", example: "9:16 and 1:1, 4K where the device allows, no platform watermark, no licensed music" },
      { token: "[DELIVERY DATE]", label: "Delivery date for finals and raw files", example: "October 10, 2026" },
      { token: "[REVISION ROUNDS]", label: "Revision rounds included in the fee", example: "one round per video" },
      { token: "[RESHOOT TERMS]", label: "When a reshoot is at the creator's cost", example: "at the Creator's cost where the brief was not followed; otherwise at an agreed day rate" },
      { token: "[USAGE TERM]", label: "Duration of the buyout across the named channels", example: "perpetual" },
      { token: "[USAGE TERRITORY]", label: "Territory of the buyout", example: "worldwide" },
      { token: "[PACKAGING OPTION FEE]", label: "Separately priced fee for packaging use", example: "$2,500 per SKU, exercisable within 24 months" },
      { token: "[EXCLUSIVITY PERIOD]", label: "How long category exclusivity runs", example: "6 months from delivery" },
      { token: "[EXCLUSIVITY CATEGORY]", label: "What counts as a competing product", example: "canned energy and functional beverages, and powdered energy mixes" },
      { token: "[FEE]", label: "Fee for the content and the buyout", example: "$1,200 for three videos, including perpetual all-media usage" },
      { token: "[PAYMENT SCHEDULE]", label: "When each portion is paid", example: "half on signature, half within 10 days of raw file delivery" },
      { token: "[PRODUCT PROVIDED]", label: "Product and materials the Company supplies", example: "one 12-can case of the current pack plus the shot brief" },
      { token: "[GOVERNING LAW STATE]", label: "Governing law state", example: "Delaware" },
      { token: "[VENUE]", label: "Venue for disputes", example: "Wilmington, Delaware" },
    ],

    body: `This UGC Creator Agreement (this "Agreement") is entered into as of [EFFECTIVE DATE] (the "Effective
Date") by and between:

  [COMPANY NAME], a [COMPANY STATE] [COMPANY ENTITY TYPE], with a principal place of
  business at [COMPANY ADDRESS], which markets products under the brand [BRAND NAME]
  (the "Company"); and

  [CREATOR NAME], with an address at [CREATOR ADDRESS] (the "Creator").

1. WHAT THIS AGREEMENT IS
The Company is commissioning the Creator to produce content for the Company's own use. The Creator
is not required to post any of it. Nothing in this Agreement obliges the Creator to publish the
Content on its own channels, to promote the Company, or to identify itself publicly as working with
the brand, and the fee is not paid for any such posting.

If the parties later agree that the Creator will also post the Content on its own channels, that is
agreed separately in writing, and Section 9 then applies to it.

2. THE CONTENT
The Creator shall produce the items described as [CONTENT DELIVERABLES] in Schedule A (the
"Content"), to the brief attached to Schedule A, meeting [TECHNICAL SPEC], and shall deliver by
[DELIVERY DATE].

The Content shall be filmed with the product supplied by the Company, in its current packaging, with
no competing product visible in frame, no third-party brand marks or packaging visible, no
platform-licensed or commercially licensed music, and no platform watermark or in-app caption burned
into the file. The Creator shall not use any third-party font, stock asset, music track or sound
effect without the Company's prior written consent, and shall list anything it does use on delivery.

3. DELIVERY, INCLUDING RAW AND UNEDITED FILES
The Creator shall deliver: the final edited files in each required format and aspect ratio; every
hook, opening line or alternate version stated in Schedule A; all raw and unedited footage and
stills captured for the Content, including takes and material that did not make the edit; the audio
recorded separately where separate audio exists; any project, edit or caption files; and a written
list of any third-party asset used. Delivery is by a method the Company can retain and re-download,
not by a link that expires. Files carrying a watermark, a platform export overlay or a resolution
below the specification are not delivery.

The Creator shall retain the raw material until the Company confirms receipt and shall re-supply any
delivered file on request for as long as it holds it.

4. REVISIONS AND RESHOOTS
The fee includes [REVISION ROUNDS]. A round is a single consolidated set of the Company's comments.
Where the Content does not follow the brief, the technical specification or the requirements in
Section 2, the Creator shall correct it at its own cost. Otherwise [RESHOOT TERMS] apply.

5. OWNERSHIP AND ASSIGNMENT
The Creator hereby irrevocably assigns to the Company all right, title and interest worldwide in and
to the Content and the raw material, including all copyrights and other intellectual property rights
in it, together with the right to sue for and recover damages for any past, present or future
infringement. This is a present assignment, effective as each item is created, and it is not a
promise to assign in the future. Ownership is not conditional on payment; any unpaid amount is a
debt owed to the Creator and is not a basis for withholding, revoking or re-taking ownership of, or
access to, the Content.

To the extent any right cannot be assigned, the Creator grants the Company an exclusive, worldwide,
perpetual, irrevocable, fully paid, transferable and sublicensable licence to exercise it. To the
extent permitted, the Creator waives, and agrees never to assert against the Company or those
claiming through it, any right of attribution or integrity or any similar right, however described,
in the Content, and where such a right cannot be waived the Creator agrees not to enforce it and
consents to the Company editing, re-cutting, recolouring, subtitling, combining and otherwise
modifying the Content and using it without credit.

6. WHAT THE COMPANY MAY DO WITH IT
For [USAGE TERM] and throughout [USAGE TERRITORY], the Company may use, reproduce, edit, re-cut,
shorten, subtitle, translate, localise, overlay, excerpt, combine with other material, create
derivative works from, publish, distribute, display and perform the Content and the raw material,
including in:

(a) paid advertising on any platform or network, including paid social, video, display, search,
    audio, connected television, retail media and marketplace advertising;
(b) organic posting on the Company's own social accounts, handles and channels;
(c) the Company's website, landing pages, blog and help content;
(d) retailer, distributor and marketplace listings, product detail pages, retailer portals and
    retailer-owned media;
(e) email, SMS and other direct marketing;
(f) trade and shopper materials, sell sheets, distributor decks, booth graphics and in-store
    displays; and
(g) internal, investor and press materials.

The Company may sublicense these uses to its agencies, distributors, retailers, marketplaces and
licensees, and may transfer them to a successor. The Company is not obliged to use the Content at
all.

7. PACKAGING — A SEPARATELY PRICED OPTION
Use of the Content, or of the Creator's image or likeness taken from it, on or in product packaging,
labels, cartons, shippers or packaging inserts is not included in Section 6.

The Company may exercise an option to add that use by giving the Creator written notice and paying
[PACKAGING OPTION FEE]. On exercise, the rights in Section 6 extend to packaging use for the same
term and territory, and the Creator shall not be entitled to any further payment for it. The Creator
shall keep the option open for the period stated in Schedule A. Once artwork carrying the Content
has gone to print, the Company may continue to sell and distribute that inventory, and its
distributors and retailers may continue to sell it, until it is exhausted.

8. LIKENESS AND RELEASES
The Creator grants the Company the right to use its name, voice, image, likeness, performance and
biographical description as captured in the Content and raw material, for the same term, territory
and channels as Section 6, and, if the option in Section 7 is exercised, for packaging as well. This
grant is as broad as the grant in Section 6 and is not limited to any narrower channel, term or
territory.

The Creator shall obtain, before filming, a written release on terms at least as broad as this
Section from every other identifiable person appearing or audible in the Content, including any
person under the age of majority, from the parent or guardian in that case, and shall deliver copies
with the Content. Where the Content is filmed in a location that is not the Creator's own home or a
public place, the Creator shall obtain a written location release.

9. DISCLOSURE IF THE CREATOR ALSO POSTS
If the parties agree in writing that the Creator will post the Content on its own channels, the
Creator shall clearly and conspicuously disclose the paid relationship in each post, in the content
itself as well as in any platform disclosure tool, in accordance with applicable disclosure
requirements, the platform's rules and the Company's written disclosure guidelines. The same applies
to any post the Creator makes about the Company or the product on its own initiative while this
Agreement is in force. Questions about what a particular requirement demands are for the Company's
counsel, and the Creator shall raise anything it is unsure about before posting.

10. BRIEF, CLAIMS AND PROHIBITED CONTENT
The Creator shall describe the product only using language the Company has approved in writing and
shall follow the brief, including its required language and its do-not-say list. The Creator shall
not state or imply that the product treats, prevents, cures or reduces the risk of any condition;
shall not compare the product to a named competitor; shall not present any personal-results,
before-and-after, weight, body-composition, performance or health outcome unless the Company has
approved that specific framing in writing; and shall not state that the product is suitable for any
allergen, dietary or medical requirement beyond the approved language.

The Content shall not depict consumption by anyone under the age the brief specifies, excessive or
unsafe consumption, consumption while driving or operating machinery, alcohol, tobacco, vaping or
controlled substances, medical or clinical framing, or sexually explicit, violent, discriminatory or
hateful material. The Creator shall not name a retailer as stocking the product unless the Company
confirms in writing that it is on shelf there.

11. EXCLUSIVITY
For [EXCLUSIVITY PERIOD], the Creator shall not produce content for, or appear in content promoting,
any product within [EXCLUSIVITY CATEGORY], whether as commissioned content, a sponsored post,
gifted content or an affiliate arrangement.

Separately and without time limit, the Creator shall not use, post, license, resell or repurpose the
Content, the raw material, the Company's brief, script or approved language, or any outtake from the
shoot, for itself or for any third party, and shall not produce content for any other party that
reproduces the concept, script, setting or framing developed for this shoot.

12. FEE, PRODUCT AND PAYMENT
The Company shall pay the Creator [FEE], on the terms set out in [PAYMENT SCHEDULE]. The fee covers
production of the Content and the rights granted in Sections 5, 6, 8 and 11. Final payment is due
after delivery of the final files and the complete raw material required by Section 3. The Company
shall supply [PRODUCT PROVIDED] at its own cost; product supplied is for use in producing the
Content and is not for resale. The Creator is responsible for its own taxes on the fee and on the
value of any product received, and supplies its own equipment, location and editing tools.

13. WARRANTIES AND CONFIDENTIALITY
The Creator represents and warrants that: it has full authority to enter into this Agreement and is
not bound by any exclusive or management arrangement that conflicts with it; the Content and the raw
material are original to the Creator and created for the Company, and have not been supplied to any
other party; the Content contains no third-party material other than what the Creator has disclosed
in writing; the Content does not, to the best of the Creator's knowledge, infringe or misappropriate
the rights of any third party; it holds the releases required by Section 8; and it is of the age of
majority where it resides.

The Creator shall keep confidential the brief, the approved language, the terms of this Agreement,
any unreleased product, packaging or launch plan, and any performance data the Company shares, and
shall not post about the engagement without the Company's written consent.

14. TERM AND TERMINATION
This Agreement begins on the Effective Date and continues until the Content is delivered and paid
for; the grants in Sections 5, 6, 7 and 8 and the obligations in Sections 11, 13, 15 and 16 continue
according to their terms. Either party may end this Agreement on written notice if the other
materially breaches it. Termination does not affect ownership of, or rights in, anything already
created, and does not entitle the Creator to reclaim the Content or the raw material.

15. GOVERNING LAW AND VENUE
This Agreement is governed by the laws of the State of [GOVERNING LAW STATE], without regard to its
conflict of laws principles. The parties consent to venue in [VENUE].

16. GENERAL
The Creator is an independent contractor and not the Company's employee, partner or agent, and has
no authority to bind the Company. This Agreement, together with Schedule A and the brief, is the
entire agreement between the parties regarding its subject matter and supersedes all prior
discussions, rate cards and platform marketplace terms. It may be amended only in a writing signed
by both parties. The Creator may not assign this Agreement; the Company may assign it to a successor
in connection with a merger or a sale of substantially all of its assets. If any provision is held
unenforceable, the remainder continues in effect. This Agreement may be signed in counterparts,
including by electronic signature, each of which is an original.

IN WITNESS WHEREOF, the parties have executed this Agreement as of the Effective Date.


[COMPANY NAME]                            [CREATOR NAME]


By: _______________________________       By: _______________________________

Name: _____________________________       Name: _____________________________

Title: ____________________________       Title: ____________________________

Date: _____________________________       Date: _____________________________


─────────────────────────────────────────────────────────────────────────────
SCHEDULE A — CONTENT, SPECIFICATION AND OPTIONS
─────────────────────────────────────────────────────────────────────────────
Content deliverables, including hook and version count:

[CONTENT DELIVERABLES]

_____________________________________________________________________________

Technical specification:

[TECHNICAL SPEC]

_____________________________________________________________________________

Raw material to be delivered (list every category, not "raws"):

_____________________________________________________________________________

Packaging option (Section 7) — period during which the Company may exercise it:

_____________________________________________________________________________

Attach: the written brief, the approved claim language, the do-not-say list and the shot list.

Creator initials: __________          Company initials: __________
`,
  },

  {
    id: "photography-agreement",
    docId: "photography-agreement",
    title: "COMMERCIAL PHOTOGRAPHY AGREEMENT",
    version: "1.0",
    lastReviewed: "2026-08-25",

    founderGuide: {
      useThisWhen:
        "Before any commercial shoot — product on white, lifestyle, packaging hero, founder portraits, in-store or event coverage — and before you book a studio, a stylist or anyone who will appear on camera.",
      whyItMatters:
        "The default in commercial photography is a licence, not a transfer, and licences are written channel by channel. That collides badly with how CPG images actually travel: one product shot ends up on the website, on a marketplace listing, inside a retailer's own portal, in a distributor's catalogue, on a sell sheet, in a paid ad, on a shelf talker and sometimes on the pack — and most of those destinations are uploaded by someone else, using files you sent them. A licence scoped to 'social and web' does not reach any of that. The other half is editing: retailers and marketplaces publish their own image specifications and revise them, so without RAW files and an express right to crop, retouch and recolour, every spec change is a new shoot.",
      negotiateCarefully: [
        {
          point: "Name every destination, including the ones you do not control",
          detail:
            "Write out packaging, retailer portals, marketplace listings, distributor sell sheets and catalogues, trade and shopper materials, paid advertising and out-of-home — and add an express right for your distributors and retailers to reproduce the images in their own channels. Routine trade activity should not require you to re-read the licence.",
        },
        {
          point: "RAW files and an unrestricted right to edit",
          detail:
            "Ask for the RAW captures and outtakes, not just the retouched selects, plus the right to crop, retouch, recolour, composite and create derivatives without further consent. This is what lets you meet a changed retailer image spec, extend a shot to a new flavour and refresh a listing without going back to the photographer.",
        },
        {
          point: "Term should match the life of the pack, not the calendar",
          detail:
            "A one- or two-year licence expires while the SKU is still selling and listings are still live, and marketplace pages do not care whose licence lapsed. Buy an assignment for hero product and packaging imagery, and if budget forces a licence elsewhere, tie it to the life of the pack generation rather than a fixed number of months.",
        },
        {
          point: "Releases are the photographer's job, in writing",
          detail:
            "Every recognisable face, private location, artwork, vehicle and piece of branded equipment in frame carries a clearance question the photography licence does not answer. Make obtaining and delivering model, property and location releases an express obligation, scoped as widely as your usage grant, with copies handed over alongside the images.",
        },
        {
          point: "Agree now what a use outside the grant costs",
          detail:
            "You will want something the original grant did not cover — packaging, a new territory, a licensing partner's catalogue. Put an extension mechanism in the document with a price agreed today, and a reshoot rate for the pack change that is coming, rather than negotiating both at the moment you need them.",
        },
      ],
      redFlags: [
        "A licence silent on paid media, on packaging, or on republication by retailers and distributors.",
        "A term shorter than the expected life of the SKU, with no agreed extension price.",
        "RAW files withheld, or delivery limited to flattened retouched selects.",
        "No right to crop, retouch, recolour or create derivatives.",
        "No obligation on the photographer to obtain and deliver model, property and location releases.",
        "Mandatory credit on every use, including packaging, retailer listings and paid advertising.",
      ],
      timeToComplete: "25 minutes to fill · settle the usage grant before the shoot is booked",
    },

    placeholders: [
      { token: "[EFFECTIVE DATE]", label: "Effective date", example: "September 1, 2026" },
      { token: "[COMPANY NAME]", label: "Your legal entity name", brandKey: "legalEntityName", example: "Acme Beverage Co., LLC" },
      { token: "[COMPANY STATE]", label: "Your state of formation", brandKey: "stateOfFormation", example: "Delaware" },
      { token: "[COMPANY ENTITY TYPE]", label: "Your entity type", example: "limited liability company" },
      { token: "[COMPANY ADDRESS]", label: "Your address", brandKey: "principalAddress", example: "123 Main St, Austin, TX 78701" },
      { token: "[BRAND NAME]", label: "Your brand name", brandKey: "tradeName", example: "Acme" },
      { token: "[PHOTOGRAPHER NAME]", label: "Photographer's or studio's legal name", example: "Grey Table Studio LLC" },
      { token: "[PHOTOGRAPHER ADDRESS]", label: "Photographer's address", example: "301 Foundry Ln, Nashville, TN 37209" },
      { token: "[SHOOT DESCRIPTION]", label: "What is being shot", example: "packaging hero, product on white and lifestyle imagery for a four-SKU canned tea line" },
      { token: "[SHOOT DATES]", label: "Shoot dates and location", example: "October 2-3, 2026, at the photographer's studio" },
      { token: "[SHOT LIST]", label: "Shot list (Schedule A)", example: "4 hero pack shots, 8 on-white per SKU, 12 lifestyle, 6 flat lays, 4 founder portraits" },
      { token: "[SELECT COUNT]", label: "Number of retouched selects delivered", example: "40 retouched images, selected by the Company" },
      { token: "[RETOUCHING SCOPE]", label: "What retouching is included", example: "clean-up, colour correction to the pack standard, background removal on on-white shots" },
      { token: "[DELIVERY DATE]", label: "Delivery date for selects and RAW files", example: "October 17, 2026" },
      { token: "[DELIVERY FORMATS]", label: "File formats and specifications for delivery", example: "layered TIFF, high-res JPEG and PNG with transparency, plus original RAW" },
      { token: "[USAGE TERM]", label: "Duration of the usage grant", example: "perpetual" },
      { token: "[USAGE TERRITORY]", label: "Territory of the usage grant", example: "worldwide" },
      { token: "[EXTENSION FEE BASIS]", label: "Agreed price for uses outside the original grant", example: "$750 per image per additional use category" },
      { token: "[RESHOOT RATE]", label: "Agreed rate for a reshoot after a pack change", example: "$2,400 per half day plus materials" },
      { token: "[FEE]", label: "Fee for the shoot and the rights granted", example: "$9,000 including two shoot days, retouching and the usage grant" },
      { token: "[PAYMENT SCHEDULE]", label: "When each portion is paid", example: "half on booking, half within 15 days of final delivery" },
      { token: "[PRODUCTION COSTS]", label: "Who pays studio, stylist, props and talent", example: "the Company, against approved estimates, at cost and without markup" },
      { token: "[POSTPONEMENT TERMS]", label: "What is payable if the shoot moves or is cancelled", example: "no charge outside 7 days; 50% of the day rate inside 7 days" },
      { token: "[ARCHIVE PERIOD]", label: "How long the photographer keeps a backup of the RAW files", example: "3 years from delivery" },
      { token: "[GOVERNING LAW STATE]", label: "Governing law state", example: "Delaware" },
      { token: "[VENUE]", label: "Venue for disputes", example: "Wilmington, Delaware" },
    ],

    body: `This Commercial Photography Agreement (this "Agreement") is entered into as of [EFFECTIVE DATE] (the
"Effective Date") by and between:

  [COMPANY NAME], a [COMPANY STATE] [COMPANY ENTITY TYPE], with a principal place of
  business at [COMPANY ADDRESS], which markets products under the brand [BRAND NAME]
  (the "Company"); and

  [PHOTOGRAPHER NAME], with an address at [PHOTOGRAPHER ADDRESS] (the "Photographer").

1. THE SHOOT
The Company engages the Photographer to photograph [SHOOT DESCRIPTION] on [SHOOT DATES] (the
"Shoot"). The Photographer shall shoot to the shot list in Schedule A and to the Company's written
brief, and shall shoot the pack in the exact condition and orientation the Company specifies,
including label alignment, fill level, condensation, date coding and any certification or claim mark
that must be legible.

The Photographer shall provide the crew, equipment, lighting and post-production described in
Schedule A. [PRODUCTION COSTS] covers studio hire, stylists, home economists, props, wardrobe,
talent, permits and location fees; costs are approved in writing in advance against an estimate and
charged at cost without markup unless Schedule A states otherwise.

2. THE IMAGES AND WHAT IS DELIVERED
"Images" means every photograph, frame, capture, scan and still taken on or for the Shoot, including
selects, alternates, outtakes, unused frames, test and lighting frames, and every version of them.

By [DELIVERY DATE] the Photographer shall deliver:
(a) [SELECT COUNT], retouched to the scope in [RETOUCHING SCOPE], in [DELIVERY FORMATS];
(b) all original RAW capture files, unaltered, with their embedded metadata intact;
(c) the layered retouching files, with masks and adjustment layers live, for every retouched image;
(d) contact sheets or a browsable set of every frame captured, so the Company can select from the
    whole take rather than from a pre-edited selection;
(e) the colour profile, calibration reference or colour target used, and any presets or LUTs
    applied; and
(f) copies of every release obtained under Section 6.

Delivery is by a method the Company can retain and re-download, not by a link that expires. Files
carrying a watermark or delivered at a resolution below the specification are not delivery. The
Photographer shall keep a backup of the RAW files for [ARCHIVE PERIOD] and shall re-supply any
delivered file on request during that period at no further charge.

3. OWNERSHIP AND ASSIGNMENT
The Photographer hereby irrevocably assigns to the Company all right, title and interest worldwide
in and to the Images, including all copyrights and other intellectual property rights in them,
together with the right to sue for and recover damages for any past, present or future infringement.
This is a present assignment, effective as each Image is created, and it is not a promise to assign
in the future. Ownership is not conditional on payment; any unpaid amount is a debt owed to the
Photographer and is not a basis for withholding, revoking or re-taking ownership of, or access to,
the Images.

To the extent permitted, the Photographer waives, and agrees not to assert against the Company or
those claiming through it, any right of attribution or integrity or any similar right, however
described, in the Images; where such a right cannot be waived, the Photographer agrees not to
enforce it and consents to the acts described in Section 4.

4. USAGE GRANT
For [USAGE TERM] and throughout [USAGE TERRITORY], and without further consent, payment or credit,
the Company may use, reproduce, crop, retouch, recolour, composite, resize, reformat, combine with
other material, create derivative works from, publish, distribute and display the Images in every
medium now known or later developed, including:

(a) primary and secondary product packaging, labels, cartons, shippers, packaging inserts and
    on-pack promotional material;
(b) retailer portals, new-item submission forms, retailer-owned media, retailer websites and
    retailer print and digital circulars;
(c) marketplace and e-commerce listings, product detail pages, storefronts and enhanced brand
    content;
(d) distributor and broker sell sheets, catalogues, order guides, price lists and presentations;
(e) trade and shopper materials, shelf talkers, case cards, coolers, displays, booth graphics and
    trade show collateral;
(f) paid advertising in any channel, including paid social, video, display, search, audio, connected
    television, retail media and marketplace advertising;
(g) out-of-home, including billboards, transit, vehicle wraps and event signage;
(h) the Company's website, landing pages, email, SMS, owned social channels and organic content; and
(i) press, investor, internal and recruitment materials.

The Company may sublicense and permit these uses by its distributors, brokers, retailers,
marketplaces, agencies, licensees and successors, including permitting them to reproduce the Images
in their own catalogues, portals, listings and marketing, and may transfer these rights to a
successor. The Company is not obliged to use any Image.

5. USES OUTSIDE THIS GRANT
If the Company wishes to use the Images in a way this Agreement does not cover — for example a new
territory beyond [USAGE TERRITORY], a use beyond [USAGE TERM] where the grant is time-limited, or a
licence to a third party for that party's own products — the Company may add it by giving written
notice and paying [EXTENSION FEE BASIS], and the Photographer shall grant the additional use on
those terms. The parties shall record the extension in writing. Nothing in this Section limits
Section 3, and where Section 3 has transferred ownership no further payment is due for any use.

If the Company's packaging, formulation or pack format changes and it needs the Shoot reproduced
with the new pack, the Photographer shall make itself reasonably available and shall charge
[RESHOOT RATE] for the reshoot, on the same delivery and rights terms as this Agreement.

6. RELEASES — THE PHOTOGRAPHER'S OBLIGATION
Before anyone appears in frame and before any location is used, the Photographer shall obtain, in a
form the Company approves and at the Photographer's cost unless Schedule A says otherwise:
(a) a signed model release from every identifiable person appearing in the Images, including crew,
    hand models, extras and passers-by, and from the parent or guardian of any person under the age
    of majority;
(b) a signed property release for any private property, artwork, design, vehicle, branded equipment
    or distinctive object appearing in the Images; and
(c) a signed location release and any permit required for each location used.

Each release shall permit the uses in Section 4 for the same term and territory as the Company's
rights, shall be assignable and sublicensable to the same extent, and shall not be revocable in a
way that cuts across those uses. The Photographer shall deliver copies with the Images and shall not
deliver an Image for which a required release has not been obtained.

The Photographer shall keep out of frame, or clear in advance, any competing product, third-party
brand mark, packaging or logo, and shall tell the Company before the Shoot if any element in the set
carries a clearance question.

7. FEES, PAYMENT AND SCHEDULING
The Company shall pay the Photographer [FEE], on the terms set out in [PAYMENT SCHEDULE]. The fee
covers the Shoot, the retouching in Section 2 and the rights granted in Sections 3 and 4. Approved
production costs are reimbursed under Section 1. If the Shoot is postponed or cancelled,
[POSTPONEMENT TERMS] apply. If the Shoot is postponed for weather, a product delivery failure or
another reason outside either party's control, the parties shall reschedule in good faith and only
unrecoverable third-party costs are payable.

8. CONFIDENTIALITY, EMBARGO AND PORTFOLIO
The Photographer shall keep confidential all non-public information it receives, including
unreleased packaging, formulations, launch dates and pricing, and shall not publish, post, exhibit
or show any Image, behind-the-scenes footage or set photograph before the date stated in Schedule A
without the Company's prior written consent. On and after that date the Photographer may display
finished Images in its portfolio and self-promotional materials, in a form that discloses no
confidential information, and shall remove any display on the Company's written request. The Company
is not required to credit the Photographer on any use, and no credit is required on packaging,
retailer or marketplace listings, trade materials or paid advertising.

Where the Shoot takes place at a co-manufacturer's, supplier's or customer's site, the Photographer
shall comply with that site's rules, shall not photograph line configuration, equipment or other
brands' product without the Company's written approval, and shall treat everything it sees there as
Confidential Information.

9. WARRANTIES
The Photographer represents and warrants that: it has full authority to enter into this Agreement;
the Images are original and taken by the Photographer or by crew engaged by it who have assigned
their rights to it; it has obtained the releases required by Section 6; the Images do not, to the
best of the Photographer's knowledge, infringe or misappropriate the rights of any third party; it
has not granted and will not grant any right in the Images to any other party; it carries its own
insurance for its crew, equipment and public liability at the levels the location requires; and it
holds any permit required for each location.

The Photographer shall not subcontract any part of the Shoot without the Company's prior written
consent, and shall bind any approved subcontractor in writing to assignment, release and
confidentiality obligations at least as protective as Sections 3, 6 and 8.

10. TERM AND TERMINATION
This Agreement begins on the Effective Date and continues until the Images are delivered and paid
for; the grants in Sections 3 and 4 and the obligations in Sections 6, 8, 9, 11 and 12 continue
according to their terms. Either party may end this Agreement on written notice if the other
materially breaches it. Termination does not affect ownership of, or rights in, any Image already
created, and the Photographer shall deliver everything captured to the termination date in the
formats required by Section 2.

11. GOVERNING LAW AND VENUE
This Agreement is governed by the laws of the State of [GOVERNING LAW STATE], without regard to its
conflict of laws principles. The parties consent to venue in [VENUE].

12. GENERAL
The Photographer is an independent contractor and not the Company's employee, partner or agent.
This Agreement, together with Schedule A, is the entire agreement between the parties regarding its
subject matter and supersedes all prior discussions, estimates, quotes and standard terms, including
any terms printed on an invoice or delivery note. It may be amended only in a writing signed by both
parties. The Photographer may not assign this Agreement without the Company's prior written consent;
the Company may assign it to a successor in connection with a merger or a sale of substantially all
of its assets. If any provision is held unenforceable, the remainder continues in effect. This
Agreement may be signed in counterparts, including by electronic signature, each of which is an
original.

IN WITNESS WHEREOF, the parties have executed this Agreement as of the Effective Date.


[COMPANY NAME]                            [PHOTOGRAPHER NAME]


By: _______________________________       By: _______________________________

Name: _____________________________       Name: _____________________________

Title: ____________________________       Title: ____________________________

Date: _____________________________       Date: _____________________________


─────────────────────────────────────────────────────────────────────────────
SCHEDULE A — SHOT LIST, DELIVERY AND PRODUCTION
─────────────────────────────────────────────────────────────────────────────
List every shot by SKU, angle, orientation and intended destination, so the on-white frames meet the
marketplace and retailer specifications and the pack faces the way the listing requires. Note which
shots are destined for packaging, since those are decided before the Shoot, not after.

Shot list:

[SHOT LIST]

_____________________________________________________________________________

_____________________________________________________________________________

Retailer, marketplace or portal specifications the on-white shots must meet:

_____________________________________________________________________________

Delivery: selects, retouching scope, formats and RAW package:

_____________________________________________________________________________

Crew, studio, stylist, props and talent — who books and who pays:

_____________________________________________________________________________

Embargo date for portfolio and behind-the-scenes use: ________________________

Photographer initials: __________          Company initials: __________
`,
  },

  {
    id: "packaging-design-agreement",
    docId: "packaging-design-agreement",
    title: "PACKAGING DESIGN AGREEMENT",
    version: "1.0",
    lastReviewed: "2026-08-25",

    founderGuide: {
      useThisWhen:
        "Before any artwork work starts on a label, can, carton, pouch or bottle — and again before each new flavour, size or format, unless this agreement already covers extensions.",
      whyItMatters:
        "This is where CPG differs from every other design brief, and the difference is the file. A flat JPEG or a print-ready PDF is a snapshot of one SKU at one moment. The dieline and the layered working file are the packaging system: the next flavour, the next size, the co-packer who needs a panel moved, the reformulation that changes the ingredient statement and the nutrition panel all require the editable file. A designer who delivers only flattened artwork holds your entire packaging system, and every future change — including the ones you are obliged to make — runs back through them at their price and their timeline. Paying the invoice does not move the file, and on its own it does not move ownership either.",
      negotiateCarefully: [
        {
          point: "The dieline is a deliverable, and it is named",
          detail:
            "The dieline comes from the can maker, carton converter or label printer and is specific to their tooling. Require it in editable vector form together with the supplier specification it was built to, and require the design to be built on the actual supplier dieline before concepts are approved. Without it in your hands, changing supplier restarts the artwork from nothing.",
        },
        {
          point: "Name the working file package item by item",
          detail:
            "'Final artwork' is satisfied by a flattened PDF. Write the list: layered native files with live type, every linked asset packaged, the fonts actually used with their licences, the dieline, print-ready files and any prepress files the printer received. Then hold a meaningful final payment until that package arrives and opens cleanly on your side.",
        },
        {
          point: "The right to modify and to extend the line without the designer",
          detail:
            "You will change a panel for a reformulation and add a fourth flavour, and you should not need permission or a new quote to do either. Take an express right to modify the artwork and to create derivative SKUs, sizes and formats yourself or through anyone else, and agree extension pricing now while you still have leverage.",
        },
        {
          point: "Fonts and stock have to reach packaging, not just comps",
          detail:
            "Desktop font licences and standard stock licences frequently do not cover mass reproduction on products for resale, and the shortfall surfaces at pre-press with a press date booked. Require the written list up front and a warranty that each licence permits packaging and commercial use and can be transferred to your distributors and a future acquirer.",
        },
        {
          point: "Who is accountable for what is printed on the panel",
          detail:
            "Designers set type; they do not verify the ingredient statement, the nutrition panel, the net content or a certifier's mark usage rules. Say plainly in the document that the brand signs off on panel content and that the designer is responsible for reproducing supplied copy accurately, then build pre-press corrections and a press check into scope rather than leaving them as change orders against a booked press date.",
        },
      ],
      redFlags: [
        "Working or native files described as the designer's property, or excluded from the deliverables.",
        "No mention of the dieline anywhere in the agreement.",
        "A licence to use the artwork rather than a present assignment of it.",
        "Full payment due on approval of a flat visual, with the file package to follow later.",
        "No extension pricing for additional flavours, sizes or formats, and no right to modify without the designer.",
        "Refusal to disclose the fonts, stock and illustration the artwork depends on, or licences that stop at desktop use.",
      ],
      timeToComplete: "30 minutes to fill · settle the file list and extension pricing before work starts",
    },

    placeholders: [
      { token: "[EFFECTIVE DATE]", label: "Effective date", example: "September 1, 2026" },
      { token: "[COMPANY NAME]", label: "Your legal entity name", brandKey: "legalEntityName", example: "Acme Beverage Co., LLC" },
      { token: "[COMPANY STATE]", label: "Your state of formation", brandKey: "stateOfFormation", example: "Delaware" },
      { token: "[COMPANY ENTITY TYPE]", label: "Your entity type", example: "limited liability company" },
      { token: "[COMPANY ADDRESS]", label: "Your address", brandKey: "principalAddress", example: "123 Main St, Austin, TX 78701" },
      { token: "[BRAND NAME]", label: "Your brand name", brandKey: "tradeName", example: "Acme" },
      { token: "[PRODUCT CATEGORY]", label: "Your product category", brandKey: "productCategory", example: "functional beverage" },
      { token: "[DESIGNER NAME]", label: "Designer's or studio's legal name", example: "Rill Design Studio LLC" },
      { token: "[DESIGNER ADDRESS]", label: "Designer's address", example: "18 Bell Ave, Portland, OR 97209" },
      { token: "[SKUS IN SCOPE]", label: "SKUs, panels and formats in scope (Schedule A)", example: "four 12oz sleek can SKUs, front and back panels, plus the 4-pack wrap" },
      { token: "[PACK FORMAT]", label: "Pack format and substrate", example: "12oz sleek aluminium can, direct print, matte overvarnish" },
      { token: "[DIELINE SOURCE]", label: "Who supplies the dieline and from where", example: "the can supplier, obtained by the Company before concepts" },
      { token: "[PRINTER NAME]", label: "Printer or converter the files are built for", example: "Crown Cork printer specification, via the co-packer" },
      { token: "[NATIVE FILE FORMATS]", label: "Required native and working file formats", example: "layered .ai with live type, packaged links, .pdf/x print-ready, editable .ai dieline" },
      { token: "[CONCEPT ROUNDS]", label: "Initial concept routes presented", example: "three routes at first presentation" },
      { token: "[REVISION ROUNDS]", label: "Revision rounds included in the fee", example: "two rounds per SKU after route selection" },
      { token: "[ADDITIONAL REVISION RATE]", label: "Rate for revisions beyond those rounds", example: "$140 per hour, approved in writing before the work" },
      { token: "[EXTENSION PRICING]", label: "Agreed price for additional flavours, sizes or formats", example: "$1,200 per additional flavour on an existing format" },
      { token: "[APPROVAL MILESTONES]", label: "Approval milestones and sign-off points", example: "route selection, artwork approval, dieline check, pre-press proof, press check" },
      { token: "[FEE]", label: "Fee for the work", example: "$22,000 across four SKUs" },
      { token: "[FINAL PAYMENT SHARE]", label: "Portion of the fee held until the file package arrives", example: "the final 30% of the fee" },
      { token: "[PAYMENT PERIOD]", label: "Time to pay a correct invoice", example: "15 days from receipt" },
      { token: "[DELIVERY DATE]", label: "Date the print-ready files are due to the printer", example: "November 6, 2026" },
      { token: "[TERMINATION NOTICE PERIOD]", label: "Notice to end the project", example: "10 days" },
      { token: "[PRIOR MATERIALS]", label: "Designer's pre-existing materials (Schedule B)", example: "Rill grid system and internal production scripts" },
      { token: "[THIRD PARTY MATERIALS]", label: "Fonts, stock and illustration used (Schedule C)", example: "Font: Sabon — desktop plus packaging licence, held by Designer" },
      { token: "[PUBLIC LAUNCH DATE]", label: "Date portfolio use may begin", example: "the public launch date of the product" },
      { token: "[GOVERNING LAW STATE]", label: "Governing law state", example: "Delaware" },
      { token: "[VENUE]", label: "Venue for disputes", example: "Wilmington, Delaware" },
    ],

    body: `This Packaging Design Agreement (this "Agreement") is entered into as of [EFFECTIVE DATE] (the
"Effective Date") by and between:

  [COMPANY NAME], a [COMPANY STATE] [COMPANY ENTITY TYPE], with a principal place of
  business at [COMPANY ADDRESS], which markets [PRODUCT CATEGORY] products under the brand
  [BRAND NAME] (the "Company"); and

  [DESIGNER NAME], with an address at [DESIGNER ADDRESS] (the "Designer").

1. THE PROJECT
The Company engages the Designer to create the graphic packaging artwork for [SKUS IN SCOPE], in the
format [PACK FORMAT], for production by [PRINTER NAME] (the "Project"). Schedule A sets out the
SKUs, panels, formats and the approval milestones.

"Work Product" means everything the Designer creates in the course of or arising from the Project,
including concepts, routes, layouts, illustrations, lettering, icons, patterns, colour builds,
mechanical artwork, dielines as adapted, retouching, mock-ups and every draft, rejected route and
working file created along the way.

2. THE DIELINE
The dieline is specific to the supplier's tooling and pack format and is the foundation of every
future change to the pack. [DIELINE SOURCE] supplies it. The Designer shall obtain and work from the
actual supplier dieline and the printer's or converter's current specification before any concept is
presented for approval, and shall not present artwork built on a generic or approximated template.

The Designer shall deliver the dieline as an editable vector file, on its own named layer, together
with the supplier or printer specification sheet it was built to, the safe area, bleed, seam, overlap
and distortion allowances applied, and, for a shrink sleeve or contoured pack, both the distorted
production file and the undistorted flat artwork it was generated from. The dieline as adapted for
the Project is Work Product and is assigned to the Company under Section 5.

3. DELIVERABLES — THE NATIVE FILE PACKAGE
Delivery of flattened, exported, watermarked, outlined-only or read-only files alone is not delivery.
The Designer shall deliver, in [NATIVE FILE FORMATS], a complete package (the "Native File Package")
containing:

(a) the layered native working file for each panel and SKU, with live editable type, named layers,
    all artboards and no destructive flattening;
(b) every linked asset — images, illustrations, logos, certification and claim marks, barcodes —
    supplied as separate files and packaged with the working file, at production resolution;
(c) the editable dieline and the supplier specification required by Section 2;
(d) the print-ready production files for each SKU, built to the printer's specification, including
    bleed, trapping and any imposition or step-and-repeat file supplied to the printer;
(e) the prepress files, including colour separations, spot and process colour builds, ink
    limits, overprint and knockout settings, varnish, foil, white-ink and any special-finish layers,
    together with any file the Designer supplied to or received from the printer;
(f) the fonts actually used, packaged with the file, or where the font licence does not permit the
    file to be transferred, a written list naming each font, its foundry, its version and its
    licence, so the Company can license it in its own name;
(g) the colour specification for each substrate, including the target colour reference, the build
    for the printed substrate and any approved variation between substrates;
(h) the barcode files at the correct magnification with quiet zones marked, and a note of any scan
    test performed; and
(i) a written index of the package so the Company can confirm nothing is missing.

The Designer shall deliver the Native File Package by a method the Company can retain and
re-download, not by a link that expires, and shall re-supply any file on request for as long as it
holds it. Print-ready files are due to the printer by [DELIVERY DATE].

4. DELIVERY AND FINAL PAYMENT
The Company shall pay the Designer [FEE], invoiced against the milestones in Schedule A, and shall
pay each correct and undisputed invoice within [PAYMENT PERIOD].

[FINAL PAYMENT SHARE] is payable on delivery of the complete Native File Package and the Company's
confirmation that it has been received and opens in full, with live type and linked assets intact, on
the Company's own systems. The Designer shall deliver the Native File Package on or before that
payment, and the Company shall make that payment promptly once the package is confirmed complete.
Withholding that portion is the Company's remedy for an incomplete package; it does not affect
ownership, which has already passed under Section 5.

5. OWNERSHIP — PRESENT ASSIGNMENT
The Designer hereby irrevocably assigns to the Company all right, title and interest worldwide in and
to the Work Product, including all copyrights, design rights, trademark rights and other intellectual
property rights in it, together with the right to sue for and recover damages for any past, present
or future infringement.

This is a present assignment. It operates as each item of Work Product is created and is not a
promise to assign at some later time. Ownership vests in the Company on creation and is not
conditional on payment, on delivery, on acceptance or on any characterisation of the Work Product.
Any unpaid amount is a debt owed to the Designer and is not a basis for withholding, revoking or
re-taking ownership of the Work Product, and the Designer shall not instruct any printer, converter
or supplier to withhold or destroy files on account of a fee dispute.

To the extent any right cannot be assigned, the Designer grants the Company an exclusive, worldwide,
perpetual, irrevocable, fully paid, transferable and sublicensable licence to exercise it. To the
extent permitted, the Designer waives, and agrees never to assert against the Company or those
claiming through it, any right of attribution, integrity or association or any similar right, however
described, in the Work Product; where such a right cannot be waived, the Designer agrees not to
enforce it and consents to the acts described in Section 6.

6. RIGHT TO MODIFY AND TO EXTEND THE LINE
The Company may, itself or through any other designer, agency, printer or supplier, and without the
Designer's consent, involvement or further payment: modify, adapt, recolour, re-typeset, rescale and
redraw the Work Product; change any panel content, including the ingredient statement, nutrition
panel, net content, claims, certification marks and barcode; adapt the artwork to a different pack
format, size, substrate, converter or dieline; and create derivative artwork for additional flavours,
variants, sizes, multipacks, club packs, private-label or regional versions and any future SKU.

Where the Company asks the Designer to produce an extension rather than doing it elsewhere, the
Designer shall quote at [EXTENSION PRICING] and shall not condition an extension on any change to
this Agreement. The Designer shall not use the artwork, the brand elements created for the Project,
or any distinctive element of them, for any other client.

7. THIRD-PARTY FONTS, STOCK AND ILLUSTRATION
Schedule C lists every third-party font, stock image, illustration, texture, icon set, pattern,
plug-in and template embedded in or required to use the Work Product, with the licence covering it,
who holds it, its term and its territory.

The Designer represents and warrants that each listed licence permits use on mass-reproduced primary
and secondary packaging and on products offered for resale, in unlimited print quantities, and
permits the Company's use in retailer and marketplace listings, trade and shopper materials, paid
advertising and out-of-home, and that each licence may be transferred or sublicensed to the Company's
printers, converters, co-manufacturers, distributors, retailers and successors. Where a licence must
be purchased in the Company's own name, or does not extend to packaging or resale, the Designer shall
say so in writing before the asset is used in any presented concept. The Designer shall not
incorporate any third-party material not listed on Schedule C without the Company's prior written
consent.

The Designer's own pre-existing materials listed on Schedule B are not assigned; anything not listed
on Schedule B is Work Product. Where a listed item is embedded in or necessary to use the artwork,
the Designer grants the Company a non-exclusive, worldwide, perpetual, irrevocable, fully paid,
transferable and sublicensable licence to use, reproduce, modify and create derivatives from it for
the uses in this Section.

8. CONCEPTS, REVISIONS AND APPROVAL MILESTONES
The Designer shall present [CONCEPT ROUNDS] and, after the Company selects a route, shall develop it
through [REVISION ROUNDS]. A round is a single consolidated set of the Company's comments. Revisions
beyond the included rounds, and any change to an approved brief, are charged at
[ADDITIONAL REVISION RATE] and only where the Company approves the additional work in writing before
it is done. Correcting an error, a departure from the brief, a specification breach or a defect is
not a revision round and is at the Designer's cost.

The Project proceeds through the milestones in [APPROVAL MILESTONES]. Each milestone is signed off by
the Company in writing before the next begins, and the Designer shall not release any file to a
printer, converter or co-manufacturer without the Company's written approval.

9. PANEL CONTENT AND RESPONSIBILITY
The Company is responsible for the accuracy and adequacy of the panel content it supplies, including
the ingredient statement, nutrition or supplement panel, allergen statement, net content, claims,
country of origin, contact details and any statement required to appear on the pack, and for
obtaining any review of that content it considers necessary. The Company signs off panel content at
the milestone stated in Schedule A.

The Designer is responsible for reproducing the supplied copy exactly as supplied, without editing,
re-wording, abbreviating or re-ordering it; for applying each certification or claim mark in
accordance with the usage rules the certifier supplies, and for asking for those rules rather than
working from a screenshot; for meeting the printer's specification for minimum type size, legibility,
contrast, barcode magnification and quiet zones; and for flagging to the Company anything on the
panel that appears inconsistent, illegible or missing. The Designer shall provide a proofing copy at
each milestone in a form the Company can read at production size.

10. PRINTER LIAISON AND PRE-PRESS
The Designer shall liaise directly with [PRINTER NAME] and with any converter or co-manufacturer the
Company names, shall build files to their current specification, and shall respond to their technical
queries. Included in the fee, and not chargeable as a revision round: supplying files in the
requested format, correcting files to meet the printer's specification, responding to pre-press
queries and corrections, checking and approving the printer's proof against the approved artwork, and
attending or reviewing a press check where Schedule A provides for one.

The Designer shall notify the Company immediately of any pre-press issue that affects cost, schedule
or appearance, and shall not agree any change to the approved artwork with the printer without the
Company's written approval.

11. CONFIDENTIALITY AND PORTFOLIO
The Designer shall keep confidential all non-public information it receives, including formulations,
ingredient sourcing, costs, supplier and co-manufacturer identities, forecasts, launch calendars and
unreleased packaging, shall use it only for the Project, and shall not publish, post, exhibit or show
any Work Product, concept or unreleased pack before [PUBLIC LAUNCH DATE] without the Company's prior
written consent. On and after that date the Designer may display finished artwork in its portfolio in
a form that discloses no confidential information, and shall remove any display on the Company's
written request. The Company is not required to credit the Designer on any use, and no credit appears
on the pack.

12. WARRANTIES AND SUBCONTRACTORS
The Designer represents and warrants that: it has full authority to enter into this Agreement; the
Work Product is original to it except for the material listed on Schedules B and C; the Work Product
does not, to the best of the Designer's knowledge, infringe or misappropriate the rights of any third
party; and it has not granted and will not grant any right in the Work Product to any other party.

The Designer shall not engage any subcontractor, illustrator, retoucher, letterer, photographer or
production studio without the Company's prior written consent, and before an approved subcontractor
begins shall bind it in writing to confidentiality, assignment and waiver obligations at least as
protective as Sections 5 and 11, and shall secure for the Company all rights it creates. The Designer
remains responsible for its subcontractors.

13. TERM, TERMINATION AND FURTHER ASSURANCES
This Agreement begins on the Effective Date and continues until the Project is complete. Either party
may end it on [TERMINATION NOTICE PERIOD] written notice, and either party may end it immediately if
the other materially breaches and does not cure within [TERMINATION NOTICE PERIOD] of written notice
describing the breach.

On termination the Company shall pay for work properly performed to the termination date, and the
Designer shall promptly deliver all Work Product in its then-current form, in the formats required by
Section 3, including partly finished layouts, the dieline and any files already with the printer.
Ownership of Work Product created before termination is unaffected.

The Designer shall, promptly on request and at the Company's expense, sign any document and take any
action the Company reasonably requires to obtain, perfect, record, register or enforce its rights in
the Work Product. This obligation continues after delivery and after termination. Sections 3, 5, 6,
7, 9, 11, 12, 14 and 15 survive termination.

14. GOVERNING LAW AND VENUE
This Agreement is governed by the laws of the State of [GOVERNING LAW STATE], without regard to its
conflict of laws principles. The parties consent to venue in [VENUE].

15. GENERAL
The Designer is an independent contractor and not the Company's employee, partner or agent. This
Agreement, together with its Schedules, is the entire agreement between the parties regarding its
subject matter and supersedes all prior discussions, proposals and quotes. Where it conflicts with a
proposal, statement of work or invoice, this Agreement controls on ownership, files, the dieline,
third-party material and modification rights. It may be amended only in a writing signed by both
parties. The Designer may not assign this Agreement without the Company's prior written consent; the
Company may assign it to a successor in connection with a merger or a sale of substantially all of
its assets. If any provision is held unenforceable, the remainder continues in effect. This Agreement
may be signed in counterparts, including by electronic signature, each of which is an original.

IN WITNESS WHEREOF, the parties have executed this Agreement as of the Effective Date.


[COMPANY NAME]                            [DESIGNER NAME]


By: _______________________________       By: _______________________________

Name: _____________________________       Name: _____________________________

Title: ____________________________       Title: ____________________________

Date: _____________________________       Date: _____________________________


─────────────────────────────────────────────────────────────────────────────
SCHEDULE A — SKUS, MILESTONES AND PAYMENT STAGES
─────────────────────────────────────────────────────────────────────────────
SKUs, panels and formats in scope:

[SKUS IN SCOPE]

_____________________________________________________________________________

Approval milestones, with the date the Company signs off panel content:

[APPROVAL MILESTONES]

_____________________________________________________________________________

Press check: included / not included — if included, who attends: _____________

Payment stages, keeping a meaningful final stage against the Native File Package:

_____________________________________________________________________________

Designer initials: __________          Company initials: __________


─────────────────────────────────────────────────────────────────────────────
SCHEDULE B — DESIGNER PRIOR MATERIALS
─────────────────────────────────────────────────────────────────────────────
Everything the Designer owned or created before this Project, or wholly independently of it, that is
carved out of the assignment in Section 5. Anything not listed here is Work Product and belongs to
the Company. Write "None" if there is none.

[PRIOR MATERIALS]

_____________________________________________________________________________

_____________________________________________________________________________

Designer initials: __________          Company initials: __________


─────────────────────────────────────────────────────────────────────────────
SCHEDULE C — FONTS, STOCK AND THIRD-PARTY MATERIAL
─────────────────────────────────────────────────────────────────────────────
Every font, stock image, illustration, texture, icon set, pattern, plug-in or template in or needed
for the artwork. For each one: what it is, the foundry or source, the licence, who holds it, and
whether it permits mass reproduction on packaging and on products for resale. A desktop-only font
licence is not enough for a printed pack — say so here rather than at pre-press.

[THIRD PARTY MATERIALS]

_____________________________________________________________________________

_____________________________________________________________________________

Designer initials: __________          Company initials: __________
`,
  },
];

export default MARKETING_TEMPLATES;
